import { queryOptions } from "src/queries/v4QueryOptions/queryOptions";
import ModeChangeDataPointDTO from "src/models/ModeChangeDataPointDTO";
import { ChangeInfo, FanHeaterStatus, generateFanHeaterChangeAnnotationInfo, useFanHeaterChangeHistoryQueryOptions } from "./shared";
import FanHeaterChangeTimeDatapointDTO from "src/models/FanHeaterChangeTimeDatapointDTO";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RangeValue } from "./ChartContainer";
import { AnnotationOptions } from "chartjs-plugin-annotation";
import { Dayjs } from "dayjs";
import { uniqueId } from "lodash";
import { useMemo } from "react";
import { binDBKeys } from "src/pages/binOverview/BinCommander";
import BinApiService from "src/api/BinApiService";
import { useBinInfoContext } from "src/queries/BinInfoContext";
import AutomationType from "src/consts/AutomationType";
import { BaseHeaders } from "./csv/types";

class AnnotationAdvancer {
  public annotations: ModeChangeDataPointDTO[];
  public currentAnnotation: ModeChangeDataPointDTO;
  public passedTime: string | null;

  constructor(annotations: ModeChangeDataPointDTO[])
  {
    this.annotations = annotations;
    this.currentAnnotation = this.annotations[0];
    this.passedTime = null;
  }

  public currentMode(): string | null | undefined {
    return this.currentAnnotation?.operatingMode;
  }

  public time(time: string) {
    if (this.currentAnnotation == null || this.currentAnnotation.x >= time) {
      return;
    }
      const nextAnnotation = this.annotations.find(a => a.x >= time);
      if (nextAnnotation) {
        this.currentAnnotation = nextAnnotation;
      }
  }
}

class AnnotationAdvancerFanHeaterChanges {
  public annotations: ChangeInfo[];
  public currentAnnotation: ChangeInfo;
  public passedTime: string | null;

  constructor(annotations: ChangeInfo[])
  {
    this.annotations = annotations;
    this.currentAnnotation = this.annotations[0];
    this.passedTime = null;
  }

  public fanStatus(): FanHeaterStatus | null {
    return this.currentAnnotation?.fanStatus;
  }

  public heaterStatus(): FanHeaterStatus | null {
    return this.currentAnnotation?.heaterStatus;
  }

  public time(time: string) {
    if (this.currentAnnotation == null || this.currentAnnotation.timetamp >= time) {
      return;
    }
      const nextAnnotation = this.annotations.find(a => a.timetamp >= time);
      if (nextAnnotation) {
        this.currentAnnotation = nextAnnotation;
      }
  }
}

export type BaseCsv = {
  Timestamp: string
  [key: string]: any
};

type useEnhancedWithFanChangeHistoryOptions<TData extends BaseCsv> = {
  data: Array<TData> & Array<Record<string, any>>,
  headers: BaseHeaders,
} & BaseChartQueryOptions

export function useEnhancedWithFanChangeHistory<TData extends BaseCsv>(options: useEnhancedWithFanChangeHistoryOptions<TData>) {

  const value = options.range;
  const fanHistoryChangeQueryOptions = useFanHeaterChangeHistoryQueryOptions(options.binId, value?.[0], value?.[1]);
  const fanChangeHistoryQuery = useQuery({...fanHistoryChangeQueryOptions});

  const withFanChangeHistory = enhancedWithFanChangeHistory({
    data: options.data,
    headers: options.headers,
    fanHeaterChanges: fanChangeHistoryQuery.data,
  })
  return withFanChangeHistory;
}

interface enhacementOptions<TData extends BaseCsv> {
  data: Array<TData> & Array<Record<string, any>>,
  headers: BaseHeaders,
  fanHeaterChanges: FanHeaterChangeTimeDatapointDTO[] | undefined,
}

export function enhancedWithFanChangeHistory<TData extends BaseCsv>(options: enhacementOptions<TData>) {
  const binInfo = useBinInfoContext();
  const fanLabel = "Fan";
  const heaterLabel = "Heater";
  if (options.fanHeaterChanges == null || options.data == null) {
    return {headers: options.headers, data: options.data};
  }

  const annotations = generateFanHeaterChangeAnnotationInfo(options.fanHeaterChanges);
  const advancer = new AnnotationAdvancerFanHeaterChanges(annotations);

  const formatFanHeaterLabel = (status: FanHeaterStatus | null) => {
    if (status == null) {
      return null;
    }

    switch (status) {
      case FanHeaterStatus.Off: {
        return "Off";
      }
      case FanHeaterStatus.Paused: {
        return "Paused";
      }
      case FanHeaterStatus.On: {
        return "On";
      }
    }
  }

  const newData = options.data.map((row) => {
    advancer.time(row.Timestamp)
    
    const newRow = {...row, [fanLabel]: formatFanHeaterLabel(advancer.fanStatus()), [heaterLabel]: formatFanHeaterLabel(advancer.heaterStatus())};
    return newRow;
  });
  const newHeaders: BaseHeaders = [...options.headers];
  newHeaders.push("Fan");
  if (binInfo.automationType === AutomationType.DriStack) {
    newHeaders.push("Heater");
  }

  return {headers: newHeaders, data: newData};
}

type enhacementOptionsModeHistory<TData extends BaseCsv> = {
  data: Array<TData> & Array<Record<string, any>>,
  headers: BaseHeaders,
} & BaseChartQueryOptions


export function useEnhanceWithModeChanges<TData extends BaseCsv>(options: enhacementOptionsModeHistory<TData>) {
  const modeHistoryQuery = useQuery(useModeChangeHistoryQueryOptions({binId: options.binId, range: options.range}));


  const modeHeaderName = "Mode";
  const newHeaders: [...BaseHeaders, typeof modeHeaderName] = [...options.headers, modeHeaderName];
  
  const advancer = new AnnotationAdvancer(modeHistoryQuery.data?.originalData?.data ?? []);

  const newData = options.data?.map(row => {
    advancer.time(row.Timestamp);
    let mode = advancer.currentMode();
    return {...row, [modeHeaderName]: formatOperatingMode(mode!) };

  }) ?? [];

  return {headers: newHeaders, data: newData};
}
export interface BaseChartQueryOptions {
  binId: number;
  range: RangeValue | null | undefined;
}export const useModeChangeHistory = (binId: number, startDate: Dayjs | null | undefined, endDate: Dayjs | null | undefined, options?: ModeChangeHistoryOptions) => {

  const yAxes = useMemo(() => {
    return options?.yAxes ?? ['y'];
  }, [options?.yAxes]);

  const chartQuery = useQuery(useModeChangeHistoryQueryOptions({
    range: [startDate!, endDate!],
    binId: binId,
  }));

  const uniqueAnnotations = chartQuery.data?.annotationInfo;

  const annotations = useMemo(() => uniqueAnnotations?.flatMap((datapoint, index) => {
    const content = `${datapoint.content}`;
    const _annotations: AnnotationOptions[] = [];
    const annotation: AnnotationOptions = {
      id: uniqueId('modeChange-'),
      type: 'line',
      scaleID: 'x',
      borderDash: [6, 6],
      borderWidth: 1,
      borderColor: "#aaa",
      value: datapoint.timestamp,
      drawTime: 'beforeDatasetsDraw',
      label: {
        textStrokeColor: "white",
        textStrokeWidth: 1,
        display: true,
        content: content,
        backgroundColor: 'transparent',
        rotation: 0,
        color: "black",
        position: 'start',
        z: 10,
        xAdjust(ctx, options) {
          let value = ctx.chart.ctx.measureText(content);
          return value.width / 2;
        },
        font: {
          size: 12,
        }
      }
    };
    for (const yaxis of yAxes) {
      _annotations.push({ ...annotation, yScaleID: yaxis });
    }
    return _annotations;
  }) ?? [], [chartQuery.data, uniqueAnnotations, yAxes]);

  return useMemo(() => {
    return { chartQuery, annotations, uniqueAnnotations: uniqueAnnotations };
  }, [chartQuery, annotations, uniqueAnnotations]);
};



export interface ModeChangeHistoryOptions {
    yAxes?: string[],
  }
  
  export const formatOperatingMode = (mode: string): string => {
    if (mode === "Manual") {
      return "User Control";
    }
    else if (mode === "FillBin") {
      return "Fill & Aerate"
    }
    else if (mode === "PreDry") {
      return "Pre Dry";
    }
    else if (mode === "TopDry") {
      return "Top Dry"
    }
    return mode;
  }
  const formatUniqueModeChangeHistoryChanges = (data: ModeChangeDataPointDTO[] | null | undefined) => {
  
    const annotations = data?.flatMap((datapoint, index) => {
      const mode = datapoint.operatingMode as string;
      const formattedMode = formatOperatingMode(mode);
      const content = `${formattedMode}`;
      const annotation = {
        timestamp: datapoint.x,
        content: content,
      };
      return annotation;
    });
  
    return {
        annotations: annotations,
    };
  }
  
  export interface ModeChangeHistoryQueryOptions {
    range: RangeValue | undefined,
    binId: number | null | undefined,
  }
  export const useModeChangeHistoryQueryOptions = (options: ModeChangeHistoryQueryOptions) => {
    const queryClient = useQueryClient();
    const binId = options.binId;
    const [startDate, endDate] = options.range ?? [];
  
    return queryOptions({
        queryKey: [...binDBKeys.bin(binId!), "charts", "modeChangeChart", { startDate, endDate }] as const,
        queryFn: async (q) => {
    
          const result = await BinApiService.getOperatingModeChangesDataset(binId!, q.queryKey[4].startDate?.toISOString()!, q.queryKey[4].endDate?.toISOString()!, null, q.signal);
          queryClient.removeQueries({ queryKey: q.queryKey.slice(0, -1), type: "inactive" });
    
          const originalData = result;
          const enriched = formatUniqueModeChangeHistoryChanges(result.data);
    
          return {originalData: originalData, annotationInfo: enriched.annotations};
        },
        refetchInterval: Infinity, retry: false, keepPreviousData: true,
        enabled: (binId ?? 0) > 0 && startDate != null && endDate != null,
      }
    )
  }

  /**
   * Adds mode changes and fan changes to the base data supplied for use in a csv
   */
  export const useAddDefaultAnnotationsToCsv = (options: BaseChartQueryOptions & {baseData: BaseCsv[], baseHeaders: BaseHeaders}) => {


    const withModes = useEnhanceWithModeChanges({
      data: options.baseData,
      headers: options.baseHeaders,
      binId: options.binId,
      range: options.range,
    });

  const withFanChangeHistory = useEnhancedWithFanChangeHistory({
      data: withModes.data,
      headers: withModes.headers,
      binId: options.binId,
      range: options.range,
  });

  if (options.baseData == null) {
    return {data: options.baseData, headers: options.baseHeaders};
  };

  return {data: withFanChangeHistory.data, headers: withFanChangeHistory.headers};
  }

